import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity, ArrowDown, ArrowUp, BookOpen, CalendarDays, Clock, Gauge, Loader2, RefreshCw, Search, Target, UserRound, Users, X } from 'lucide-react';
import { ApiService } from '../../services/api';
import { getThemeChromeTextStyle } from './modes/speedReadingShared';

type ReportExercise = { exerciseId: string; title: string; sectionTitle: string; count: number };

type ReportStudent = {
  userId: string;
  fullName: string;
  email: string;
  departmentId: string | null;
  departmentName: string | null;
  readingLevel: string | null;
  initialSpeed: number | null;
  latestSpeed: number | null;
  lastLoginAt: string | null;
  loginDays: number;
  activityCount: number;
  activityDays: number;
  activeSeconds: number;
  activeMinutes: number;
  lastActivityAt: string | null;
  textCount: number;
  questionTotal: number;
  correctTotal: number;
  comprehension: number | null;
  latestComprehension: number | null;
  topExercises: ReportExercise[];
};

type UsageReport = {
  days: number;
  departments: Array<{ id: string; name: string }>;
  students: ReportStudent[];
};

type UsageReportDetail = {
  days: number;
  student: {
    userId: string;
    fullName: string;
    email: string;
    departmentName: string | null;
    readingLevel: string | null;
    initialSpeed: number | null;
    currentSpeed: number | null;
    targetSpeed: number | null;
    onboardingCompleted: boolean;
    lastLoginAt: string | null;
  };
  daily: Array<{ date: string; activityCount: number; activeSeconds: number; activeMinutes: number; loggedIn: boolean; textCount: number }>;
  sections: Array<{ sectionTitle: string; activityCount: number; activeSeconds: number; activeMinutes: number }>;
  exercises: Array<{ exerciseId: string; title: string; sectionTitle: string; activityCount: number; activeSeconds: number; activeMinutes: number; lastActivityAt: string | null }>;
  results: Array<{
    date: string;
    title: string;
    sectionTitle: string;
    wpm: number | null;
    questionCount: number | null;
    correctCount: number | null;
    comprehension: number | null;
    contentTitle: string | null;
    difficultyLevel: string | null;
  }>;
  texts: Array<{ date: string; title: string; difficultyLevel: string | null; exerciseTitle: string }>;
};

type SortKey = 'name' | 'lastLogin' | 'loginDays' | 'activity' | 'minutes' | 'speed' | 'comprehension';

const DAY_OPTIONS = [7, 30, 90] as const;

const formatDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0 dk';
  if (seconds < 60) return `${Math.round(seconds)} sn`;
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} dk`;
  return rest ? `${hours} sa ${rest} dk` : `${hours} sa`;
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const formatRelativeDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (diffDays <= 0) return `Bugün ${date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
  if (diffDays === 1) return 'Dün';
  if (diffDays < 7) return `${diffDays} gün önce`;
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: diffDays > 300 ? 'numeric' : undefined });
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatDayLabel = (isoDate: string) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' });
};

const comprehensionClass = (value: number | null) => {
  if (value === null || value === undefined) return 'text-[#9aaab2]';
  if (value >= 80) return 'text-[#2f9e5b]';
  if (value >= 50) return 'text-[#c98a12]';
  return 'text-[#d0503f]';
};

const percent = (correct: number, total: number) => (total > 0 ? Math.round((100 * correct) / total) : null);

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; hint?: string }> = ({ icon, label, value, hint }) => (
  <div className="rounded-[20px] border border-[#dce7ec] bg-white px-4 py-4">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">
      <span className="text-[#5f7d8a]">{icon}</span>
      {label}
    </div>
    <div className="mt-2 text-[24px] font-black leading-none text-[#18485c]">{value}</div>
    {hint ? <div className="mt-1.5 text-xs text-[#7b909a]">{hint}</div> : null}
  </div>
);

const SpeedCell: React.FC<{ initial: number | null; latest: number | null }> = ({ initial, latest }) => {
  if (!initial && !latest) return <span className="text-[#9aaab2]">—</span>;
  if (!initial || !latest || initial === latest) return <span className="font-bold text-[#244c5b]">{latest || initial}</span>;
  const rising = latest > initial;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="text-[#8aa0a9]">{initial}</span>
      <span className="text-[#8aa0a9]">→</span>
      <span className="font-bold text-[#244c5b]">{latest}</span>
      {rising ? <ArrowUp className="h-3.5 w-3.5 text-[#2f9e5b]" /> : <ArrowDown className="h-3.5 w-3.5 text-[#d0503f]" />}
    </span>
  );
};

const DetailSection: React.FC<{ title: string; children: React.ReactNode; empty?: boolean; emptyText?: string }> = ({ title, children, empty, emptyText }) => (
  <section className="rounded-[20px] border border-[#dce7ec] bg-white p-4">
    <div className="mb-3 text-sm font-black text-[#214858]">{title}</div>
    {empty ? <div className="rounded-xl border border-dashed border-[#d7e3e8] bg-[#fbfdfe] px-4 py-5 text-sm text-[#70858e]">{emptyText || 'Bu dönemde kayıt yok.'}</div> : children}
  </section>
);

const StudentDetailDrawer: React.FC<{ userId: string; days: number; onClose: () => void }> = ({ userId, days, onClose }) => {
  const [detail, setDetail] = useState<UsageReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    ApiService.getApi(`speed-reading/admin/usage-report/${userId}?days=${days}`)
      .then((response) => { if (!cancelled) setDetail(response as UsageReportDetail); })
      .catch(() => { if (!cancelled) setError('Öğrenci ayrıntısı yüklenemedi.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, days]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const maxDailySeconds = Math.max(1, ...(detail?.daily || []).map((day) => day.activeSeconds));
  const maxSectionCount = Math.max(1, ...(detail?.sections || []).map((section) => section.activityCount));
  const activeDayCount = (detail?.daily || []).filter((day) => day.activityCount > 0).length;
  const loginDayCount = (detail?.daily || []).filter((day) => day.loggedIn).length;

  // Gövdeye taşınır: sayfa kabındaki transform'lar fixed konumu bozmasın (telefonda çekmece ekranın tamamını kaplasın).
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex justify-end bg-[#0f2b36]/30" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-[820px] flex-col overflow-hidden bg-[#f3f5f7] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#dce7ec] bg-white px-5 py-4">
          <div className="min-w-0">
            <div className="truncate text-lg font-black text-[#214858]">{detail?.student.fullName || 'Öğrenci'}</div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#7a8e98]">
              {detail?.student.departmentName ? <span className="font-semibold text-[#325867]">{detail.student.departmentName}</span> : null}
              {detail?.student.email ? <span>{detail.student.email}</span> : null}
              <span>Son {days} gün</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-[#dbe6eb] bg-white p-2 text-[#5a727d]" aria-label="Kapat">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
          {loading ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6f8792]" /></div>
          ) : error || !detail ? (
            <div className="rounded-xl border border-[#f1c9c9] bg-white px-4 py-5 text-sm text-[#c44f4f]">{error || 'Ayrıntı bulunamadı.'}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatCard icon={<CalendarDays className="h-3.5 w-3.5" />} label="Son giriş" value={formatRelativeDate(detail.student.lastLoginAt)} hint={`${loginDayCount} gün giriş · ${activeDayCount} gün çalışma`} />
                <StatCard icon={<Target className="h-3.5 w-3.5" />} label="Okuma seviyesi" value={detail.student.readingLevel || '—'} hint={detail.student.onboardingCompleted ? 'Başlangıç kurulumu tamam' : 'Başlangıç kurulumu eksik'} />
                <StatCard icon={<Gauge className="h-3.5 w-3.5" />} label="Başlangıç hızı" value={detail.student.initialSpeed ? `${detail.student.initialSpeed}` : '—'} hint="kelime/dk" />
                <StatCard icon={<Gauge className="h-3.5 w-3.5" />} label="Hedef hız" value={detail.student.targetSpeed ? `${detail.student.targetSpeed}` : '—'} hint="kelime/dk" />
              </div>

              <DetailSection title="Günlük Çalışma" empty={!detail.daily.some((day) => day.activityCount > 0 || day.loggedIn || day.textCount > 0)}>
                <div className="flex h-36 items-end gap-[3px]">
                  {detail.daily.map((day) => {
                    const height = day.activityCount > 0 ? Math.max(8, Math.round((day.activeSeconds / maxDailySeconds) * 100)) : 0;
                    return (
                      <div
                        key={day.date}
                        className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
                        title={`${formatDayLabel(day.date)} · ${day.activityCount} uygulama · ${formatDuration(day.activeSeconds)}${day.textCount ? ` · ${day.textCount} metin` : ''}${day.loggedIn ? ' · giriş var' : ''}`}
                      >
                        <div className="w-full rounded-t-[4px]" style={{ height: `${height}%`, backgroundColor: 'var(--theme-primary, #2f7188)', opacity: height ? 0.85 : 0 }} />
                        <div className={`h-1.5 w-1.5 shrink-0 rounded-full ${day.loggedIn ? 'bg-[#2f9e5b]' : 'bg-transparent'}`} />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex justify-between text-[11px] text-[#8aa0a9]">
                  <span>{formatDayLabel(detail.daily[0]?.date || '')}</span>
                  <span>{formatDayLabel(detail.daily[detail.daily.length - 1]?.date || '')}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-center gap-1.5 text-[11px] text-[#8aa0a9]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#2f9e5b]" /> giriş yapılan gün · sütun yüksekliği = çalışma süresi
                </div>
              </DetailSection>

              <DetailSection title="Bölümlere Göre" empty={detail.sections.length === 0}>
                <div className="space-y-2.5">
                  {detail.sections.map((section) => (
                    <div key={section.sectionTitle}>
                      <div className="mb-1 flex justify-between text-xs">
                        <span className="font-semibold text-[#325867]">{section.sectionTitle}</span>
                        <span className="text-[#7a8e98]">{section.activityCount} uygulama · {formatDuration(section.activeSeconds)}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-[#eef3f5]">
                        <div className="h-full rounded-full" style={{ width: `${Math.round((section.activityCount / maxSectionCount) * 100)}%`, backgroundColor: 'var(--theme-primary, #2f7188)' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </DetailSection>

              <DetailSection title="Yaptığı Uygulamalar" empty={detail.exercises.length === 0}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px] text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#8aa0a9]">
                        <th className="pb-2 font-semibold">Uygulama</th>
                        <th className="pb-2 font-semibold">Bölüm</th>
                        <th className="pb-2 text-right font-semibold">Sayı</th>
                        <th className="pb-2 text-right font-semibold">Süre</th>
                        <th className="pb-2 text-right font-semibold">Son</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.exercises.map((exercise) => (
                        <tr key={exercise.exerciseId} className="border-t border-[#eef3f5]">
                          <td className="py-2 font-semibold text-[#244c5b]">{exercise.title}</td>
                          <td className="py-2 text-[#6f8590]">{exercise.sectionTitle}</td>
                          <td className="py-2 text-right text-[#244c5b]">{exercise.activityCount}</td>
                          <td className="py-2 text-right text-[#6f8590]">{formatDuration(exercise.activeSeconds)}</td>
                          <td className="py-2 text-right text-[#6f8590]">{formatRelativeDate(exercise.lastActivityAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DetailSection>

              <DetailSection title="Okuma Hızı ve Anlama Sonuçları" empty={detail.results.length === 0} emptyText="Henüz ölçülmüş okuma hızı ya da cevaplanmış anlama sorusu yok.">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#8aa0a9]">
                        <th className="pb-2 font-semibold">Tarih</th>
                        <th className="pb-2 font-semibold">Uygulama / Metin</th>
                        <th className="pb-2 text-right font-semibold">Hız</th>
                        <th className="pb-2 text-right font-semibold">Anlama</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.results.map((result, index) => (
                        <tr key={`${result.date}-${index}`} className="border-t border-[#eef3f5] align-top">
                          <td className="py-2 whitespace-nowrap text-[#6f8590]">{formatDateTime(result.date)}</td>
                          <td className="py-2">
                            <div className="font-semibold text-[#244c5b]">{result.title}</div>
                            {result.contentTitle ? <div className="text-xs text-[#7a8e98]">{result.contentTitle}{result.difficultyLevel ? ` · ${result.difficultyLevel}` : ''}</div> : null}
                          </td>
                          <td className="py-2 text-right whitespace-nowrap text-[#244c5b]">{result.wpm ? `${result.wpm} kel/dk` : '—'}</td>
                          <td className={`py-2 text-right whitespace-nowrap font-bold ${comprehensionClass(result.comprehension)}`}>
                            {result.questionCount ? `%${result.comprehension} (${result.correctCount}/${result.questionCount})` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DetailSection>

              <DetailSection title="Son Okunan Metinler" empty={detail.texts.length === 0} emptyText="Bu öğrenciye henüz metin gösterilmemiş.">
                <div className="space-y-2">
                  {detail.texts.map((text, index) => (
                    <div key={`${text.date}-${index}`} className="flex items-start justify-between gap-3 rounded-xl border border-[#eef3f5] px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-[#244c5b]">{text.title}</div>
                        <div className="text-xs text-[#7a8e98]">{text.exerciseTitle}{text.difficultyLevel ? ` · ${text.difficultyLevel}` : ''}</div>
                      </div>
                      <div className="shrink-0 text-xs text-[#8aa0a9]">{formatDateTime(text.date)}</div>
                    </div>
                  ))}
                </div>
              </DetailSection>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const SpeedReadingUsageReportPanel: React.FC = () => {
  const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(30);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('activity');
  const [sortDesc, setSortDesc] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const loadReport = useCallback(async (dayCount: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getApi(`speed-reading/admin/usage-report?days=${dayCount}`);
      setReport(response as UsageReport);
    } catch (loadError) {
      console.error('Hızlı okuma öğrenci raporu yüklenemedi:', loadError);
      setError('Rapor yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport(days);
  }, [days, loadReport]);

  const filteredStudents = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('tr-TR');
    const items = (report?.students || []).filter((student) => {
      if (departmentId && student.departmentId !== departmentId) return false;
      if (!query) return true;
      return [student.fullName, student.email, student.departmentName].some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(query));
    });
    const valueOf = (student: ReportStudent): number | string => {
      switch (sortKey) {
        case 'name': return student.fullName.toLocaleLowerCase('tr-TR');
        case 'lastLogin': return student.lastLoginAt ? new Date(student.lastLoginAt).getTime() : 0;
        case 'loginDays': return student.loginDays;
        case 'minutes': return student.activeSeconds;
        case 'speed': return student.latestSpeed || 0;
        case 'comprehension': return student.comprehension ?? -1;
        default: return student.activityCount;
      }
    };
    return [...items].sort((a, b) => {
      const left = valueOf(a);
      const right = valueOf(b);
      const order = typeof left === 'string' ? left.localeCompare(String(right), 'tr') : left - (right as number);
      return sortDesc ? -order : order;
    });
  }, [report, search, departmentId, sortKey, sortDesc]);

  const summary = useMemo(() => {
    const questionTotal = filteredStudents.reduce((total, student) => total + student.questionTotal, 0);
    const correctTotal = filteredStudents.reduce((total, student) => total + student.correctTotal, 0);
    return {
      studentCount: filteredStudents.length,
      activeStudentCount: filteredStudents.filter((student) => student.activityCount > 0 || student.loginDays > 0).length,
      activityCount: filteredStudents.reduce((total, student) => total + student.activityCount, 0),
      activeSeconds: filteredStudents.reduce((total, student) => total + student.activeSeconds, 0),
      comprehension: percent(correctTotal, questionTotal),
    };
  }, [filteredStudents]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc((current) => !current);
      return;
    }
    setSortKey(key);
    setSortDesc(key !== 'name');
  };

  const closeDetail = useCallback(() => setSelectedUserId(null), []);

  const renderSortHeader = (label: string, sort: SortKey, align: 'left' | 'right' = 'left') => (
    <th key={sort} className={`px-3 pb-2 font-semibold ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button type="button" onClick={() => handleSort(sort)} className="inline-flex items-center gap-1 uppercase tracking-[0.08em] hover:text-[#325867]">
        {label}
        {sortKey === sort ? (sortDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />) : null}
      </button>
    </th>
  );

  return (
    <div className="bg-[#f3f5f7] p-3 sm:p-4 md:h-full md:min-h-0 lg:px-6 lg:pt-6 lg:pb-6">
      <div className="mx-auto flex max-w-[1480px] flex-col rounded-[24px] border border-[#e0e9ed] bg-[#f8fafb] p-4 shadow-sm sm:rounded-[28px] sm:p-5 md:h-full md:min-h-0">
        <div className="mb-5 flex shrink-0 flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="text-[22px] font-black sm:text-[28px]" style={getThemeChromeTextStyle()}>Öğrenci Raporu</div>
            <p className="mt-1 max-w-[760px] text-sm leading-6 text-[#6f8590]">
              Öğrencilerin ne sıklıkla girdiğini, hangi uygulamalara çalıştığını, okuma hızını ve anlama sonuçlarını buradan izleyin. Ayrıntı için öğrenciye tıklayın.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="inline-flex rounded-2xl border border-[#dce5ea] bg-white p-1">
              {DAY_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setDays(option)}
                  className="rounded-xl px-3 py-2 text-sm font-semibold transition-colors"
                  style={days === option ? { backgroundColor: 'var(--theme-primary, #2f7188)', color: '#fff' } : { color: '#5a727d' }}
                >
                  Son {option} gün
                </button>
              ))}
            </div>
            <select
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
              className="rounded-2xl border border-[#dce5ea] bg-white px-3 py-2.5 text-sm text-[#234b5a] outline-none"
            >
              <option value="">Tüm sınıflar</option>
              {(report?.departments || []).map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 rounded-2xl border border-[#dce5ea] bg-white px-3 py-2.5">
              <Search className="h-4 w-4 text-[#89a1ac]" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} className="w-full bg-transparent text-sm outline-none sm:w-44" placeholder="Öğrenci ara..." />
            </div>
            <button type="button" onClick={() => void loadReport(days)} className="inline-flex items-center justify-center rounded-2xl border border-[#dbe6eb] bg-white p-2.5 text-[#5a727d]" aria-label="Yenile">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="mb-4 grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard icon={<Users className="h-3.5 w-3.5" />} label="Öğrenci" value={String(summary.studentCount)} hint={`${summary.activeStudentCount} öğrenci bu dönemde aktif`} />
          <StatCard icon={<Activity className="h-3.5 w-3.5" />} label="Yapılan uygulama" value={String(summary.activityCount)} hint={summary.activeStudentCount ? `Aktif öğrenci başına ${Math.round(summary.activityCount / summary.activeStudentCount)}` : undefined} />
          <StatCard icon={<Clock className="h-3.5 w-3.5" />} label="Çalışma süresi" value={formatDuration(summary.activeSeconds)} />
          <StatCard icon={<BookOpen className="h-3.5 w-3.5" />} label="Ortalama anlama" value={summary.comprehension === null ? '—' : `%${summary.comprehension}`} hint="Cevaplanan anlama sorularında" />
          <StatCard icon={<CalendarDays className="h-3.5 w-3.5" />} label="Dönem" value={`${days} gün`} hint="Giriş ve çalışma günleri bu döneme göre" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col rounded-[22px] border border-[#dce7ec] bg-white p-3 sm:rounded-[24px] sm:p-4">
          {loading && !report ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6f8792]" /></div>
          ) : error ? (
            <div className="rounded-xl border border-[#f1c9c9] px-4 py-5 text-sm text-[#c44f4f]">{error}</div>
          ) : filteredStudents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#d7e3e8] bg-[#fbfdfe] px-4 py-6 text-sm text-[#70858e]">Bu filtreye uyan öğrenci yok.</div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[1060px] text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="text-[11px] text-[#8aa0a9]">
                    {renderSortHeader('Öğrenci', 'name')}
                    {renderSortHeader('Son giriş', 'lastLogin')}
                    {renderSortHeader('Giriş günü', 'loginDays', 'right')}
                    {renderSortHeader('Uygulama', 'activity', 'right')}
                    {renderSortHeader('Süre', 'minutes', 'right')}
                    <th className="px-3 pb-2 text-left font-semibold uppercase tracking-[0.08em]">En çok çalıştığı</th>
                    {renderSortHeader('Okuma hızı', 'speed', 'right')}
                    {renderSortHeader('Anlama', 'comprehension', 'right')}
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr
                      key={student.userId}
                      onClick={() => setSelectedUserId(student.userId)}
                      className="cursor-pointer border-t border-[#eef3f5] transition-colors hover:bg-[#f6fafc]"
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-[#eef5f8] p-2 text-[#476572]"><UserRound className="h-4 w-4" /></div>
                          <div className="min-w-0">
                            <div className="truncate font-bold text-[#244c5b]">{student.fullName}</div>
                            <div className="truncate text-xs text-[#7a8e98]">{[student.departmentName, student.readingLevel].filter(Boolean).join(' · ') || student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-[#4f6a76]">{formatRelativeDate(student.lastLoginAt)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <span className="font-bold text-[#244c5b]">{student.loginDays}</span>
                        <span className="text-[#8aa0a9]"> / {days}</span>
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="font-bold text-[#244c5b]">{student.activityCount}</div>
                        <div className="text-xs text-[#8aa0a9]">{student.activityDays} gün{student.textCount ? ` · ${student.textCount} metin` : ''}</div>
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap text-[#4f6a76]">{formatDuration(student.activeSeconds)}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {student.topExercises.length === 0 ? <span className="text-[#9aaab2]">—</span> : student.topExercises.slice(0, 3).map((exercise) => (
                            <span key={exercise.exerciseId} className="rounded-lg bg-[#eef5f8] px-2 py-1 text-xs font-semibold text-[#3f6170]" title={exercise.sectionTitle}>
                              {exercise.title} · {exercise.count}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right"><SpeedCell initial={student.initialSpeed} latest={student.latestSpeed} /></td>
                      <td className={`px-3 py-3 text-right whitespace-nowrap font-bold ${comprehensionClass(student.comprehension)}`}>
                        {student.comprehension === null ? '—' : `%${student.comprehension}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 shrink-0 text-xs leading-5 text-[#8aa0a9]">
            Uygulama sayısı, süre, okuma hızı ve anlama sonuçları bu güncellemeyle kaydedilmeye başladı; giriş günleri ve okunan metinler daha eskiye uzanır. 10 saniyeden kısa açılışlar uygulama sayılmaz.
          </div>
        </div>
      </div>

      {selectedUserId ? (
        <StudentDetailDrawer userId={selectedUserId} days={days} onClose={closeDetail} />
      ) : null}
    </div>
  );
};

export default SpeedReadingUsageReportPanel;
